var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { cloneElement, useMemo, isValidElement, useLayoutEffect, useRef, } from "react";
import { withPortal, } from "../../hoc/withPortal";
import useUniqueId from "../../hooks/useUniqueId";
import Text from "../text/Text";
import Tooltip from "../tooltip/Tooltip";
import mergeProps from "../../utils/mergeProps";
import useIdAttrs from "../../hooks/useIdAttrs";
import { getValueFromPixelString } from "../../utils/TokenHelpers";
import { apolloSpaceInset1X } from "@nexthink/apollo-tokens";
import { removeFalsy } from "../../utils/ObjectHelpers";
const prefixId = "nx-with-tooltip-";
const tooltipSuffixId = "-tooltip";
export const TargetContent = (_a) => {
    var { id, children, forwardedRef, onClick, onMouseEnter, onMouseLeave, onPointerEnter, onPointerLeave, onKeyDown, onFocus, onBlur, onContextMenu, tabIndex, 
    // unused props
    maxWidth, tooltipContents, spacing, hide, isVisible, tooltipPosition, show, update, trigger } = _a, rest = __rest(_a, ["id", "children", "forwardedRef", "onClick", "onMouseEnter", "onMouseLeave", "onPointerEnter", "onPointerLeave", "onKeyDown", "onFocus", "onBlur", "onContextMenu", "tabIndex", "maxWidth", "tooltipContents", "spacing", "hide", "isVisible", "tooltipPosition", "show", "update", "trigger"]);
    const eventHandlers = removeFalsy({
        onClick,
        onKeyDown,
        onFocus,
        onBlur,
        onMouseEnter,
        onMouseLeave,
        onPointerEnter,
        onPointerLeave,
        onContextMenu,
    });
    const commonProps = Object.assign(Object.assign({ ref: forwardedRef, "aria-haspopup": "dialog", "aria-hidden": false, tabIndex }, (isVisible && { "aria-describedby": id + tooltipSuffixId })), rest);
    if (typeof children === "string") {
        return (React.createElement(Text, Object.assign({}, commonProps, eventHandlers), children));
    }
    if (typeof children === "function") {
        return cloneElement(children(Object.assign(Object.assign({}, commonProps), eventHandlers)), {
            ref: forwardedRef,
        });
    }
    const mergedProps = isValidElement(children) ? mergeProps(children.props, eventHandlers) : eventHandlers;
    return cloneElement(children, Object.assign(Object.assign(Object.assign(Object.assign({}, commonProps), mergedProps), (children.props.tabIndex && { tabIndex: children.props.tabIndex })), (!children.props.id && { id: `${id}-target` })));
};
const PortalTooltipContent = ({ id, tooltipContents, tooltipPosition, spacing, maxWidth, "data-insights": insightsAttr, hide, show, isVisible, update, }) => {
    const idAttrs = useIdAttrs({ id, insightsAttr });
    const contentWrapperRef = useRef(null);
    useLayoutEffect(() => {
        if (contentWrapperRef.current) {
            const tooltipContent = contentWrapperRef.current.firstElementChild;
            // if tooltip content is a string or the first child doesn't have a max-width defined then add the default max-width
            if ((!tooltipContent || getComputedStyle(tooltipContent).getPropertyValue("max-width") === "none") && maxWidth) {
                const maxWidthWithoutPadding = (typeof maxWidth === "string" ? getValueFromPixelString(maxWidth) : maxWidth) -
                    2 * getValueFromPixelString(apolloSpaceInset1X);
                contentWrapperRef.current.style.maxWidth = `${maxWidthWithoutPadding}px`;
            }
        }
    }, [contentWrapperRef.current, maxWidth]);
    const content = typeof tooltipContents === "function"
        ? tooltipContents({ hide, isVisible, show, update, tooltipPosition })
        : tooltipContents;
    return (React.createElement(Tooltip, Object.assign({}, idAttrs(tooltipSuffixId), { position: tooltipPosition, spacing: spacing }),
        React.createElement("div", { ref: contentWrapperRef }, content)));
};
const WithTooltip = (_a) => {
    var { tooltipContents, spacing = "compact", alignment = "bottomLeft", position = "absolute", trigger = "hover", hide = true, offset = [0, 10], maxWidth = "400px", flip = true, shouldHideWhenTargetHidden = true, shouldCloseWhenTargetHidden = false, disablePortal = false, children } = _a, rest = __rest(_a, ["tooltipContents", "spacing", "alignment", "position", "trigger", "hide", "offset", "maxWidth", "flip", "shouldHideWhenTargetHidden", "shouldCloseWhenTargetHidden", "disablePortal", "children"]);
    const tooltipId = useUniqueId(prefixId);
    // destructure offset for use in useMemo dependency array
    const [offsetSkidding, offsetDistance] = offset;
    const TargetWithPortal = useMemo(() => {
        return withPortal(PortalTooltipContent, Object.assign({ alignment,
            position,
            offset,
            trigger,
            flip,
            hide,
            shouldHideWhenTargetHidden,
            shouldCloseWhenTargetHidden,
            disablePortal, useFocusTrap: false, dialog: {
                label: "Tooltip",
            } }, rest))(TargetContent);
    }, [
        alignment,
        position,
        offsetSkidding,
        offsetDistance,
        trigger,
        flip,
        hide,
        shouldHideWhenTargetHidden,
        shouldCloseWhenTargetHidden,
        disablePortal,
    ]);
    return (React.createElement(TargetWithPortal, Object.assign({ id: tooltipId, tooltipContents: tooltipContents, spacing: spacing, maxWidth: maxWidth, trigger: trigger }, rest), children));
};
export default WithTooltip;
//# sourceMappingURL=WithTooltip.js.map